import type { AppError, ErrorSeverity } from '@shared/error';

export interface NotificationOptions {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  dismissible?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

class NotificationManager {
  private container: HTMLElement | null = null;
  private notifications: Map<string, HTMLElement> = new Map();
  private nextId = 1;

  init(): void {
    if (this.container) return;
    this.container = document.createElement('div');
    this.container.id = 'notification-container';
    this.container.style.cssText = `position: fixed; top: 20px; right: 20px; z-index: 10000; max-width: 400px; pointer-events: none;`;
    document.body.appendChild(this.container);
  }

  show(options: NotificationOptions): string {
    this.init();
    const id = `notification-${this.nextId++}`;
    const el = this.createNotification(id, options);
    this.container?.appendChild(el);
    this.notifications.set(id, el);
    setTimeout(() => { el.style.opacity = '1'; el.style.transform = 'translateX(0)'; }, 10);
    if (options.duration && options.duration > 0) setTimeout(() => this.dismiss(id), options.duration);
    return id;
  }

  dismiss(id: string): void {
    const el = this.notifications.get(id);
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateX(100%)';
    setTimeout(() => { el.remove(); this.notifications.delete(id); }, 300);
  }

  dismissAll(): void {
    for (const id of this.notifications.keys()) this.dismiss(id);
  }

  error(message: string, duration = 5000, options?: Partial<NotificationOptions>): string {
    return this.show({ message, type: 'error', duration, dismissible: true, ...options });
  }
  success(message: string, duration = 3000, options?: Partial<NotificationOptions>): string {
    return this.show({ message, type: 'success', duration, dismissible: true, ...options });
  }
  warning(message: string, duration = 4000, options?: Partial<NotificationOptions>): string {
    return this.show({ message, type: 'warning', duration, dismissible: true, ...options });
  }
  info(message: string, duration = 3000, options?: Partial<NotificationOptions>): string {
    return this.show({ message, type: 'info', duration, dismissible: true, ...options });
  }

  showAppError(error: AppError): string {
    const duration = this.getDurationForSeverity(error.severity);
    const dismissible = error.severity !== 'CRITICAL';
    return this.show({ message: error.userMessage, type: 'error', duration, dismissible });
  }

  private createNotification(id: string, options: NotificationOptions): HTMLElement {
    const wrap = document.createElement('div');
    wrap.id = id;
    wrap.style.cssText = `background: ${this.getBackgroundColor(options.type)}; color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 12px; box-shadow: 0 4px 12px rgba(0,0,0,.15); display: flex; align-items: center; justify-content: space-between; gap: 12px; pointer-events: auto; opacity: 0; transform: translateX(100%); transition: opacity .3s ease, transform .3s ease; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; line-height: 1.5;`;
    const icon = document.createElement('span');
    icon.textContent = this.getIcon(options.type);
    icon.style.cssText = 'font-size: 20px; flex-shrink: 0;';
    const msg = document.createElement('div');
    msg.textContent = options.message;
    msg.style.cssText = 'flex: 1; min-width: 0;';
    wrap.appendChild(icon);
    wrap.appendChild(msg);
    if (options.actionLabel && options.onAction) {
      const btn = document.createElement('button');
      btn.textContent = options.actionLabel;
      btn.style.cssText = `background: rgba(255,255,255,.2); border: 1px solid rgba(255,255,255,.3); color: inherit; padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 600;`;
      btn.onclick = () => { options.onAction?.(); this.dismiss(id); };
      wrap.appendChild(btn);
    }
    if (options.dismissible !== false) {
      const close = document.createElement('button');
      close.textContent = '×';
      close.style.cssText = `background: none; border: none; color: inherit; font-size: 24px; line-height: 1; cursor: pointer; padding: 0; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; opacity: .7;`;
      close.onclick = () => this.dismiss(id);
      wrap.appendChild(close);
    }
    return wrap;
  }

  private getBackgroundColor(type: NotificationOptions['type']): string {
    switch (type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  }

  private getIcon(type: NotificationOptions['type']): string {
    switch (type) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return '•';
    }
  }

  private getDurationForSeverity(severity: ErrorSeverity): number {
    switch (severity) {
      case 'LOW': return 2000;
      case 'MEDIUM': return 5000;
      case 'HIGH': return 8000;
      case 'CRITICAL': return 0;
      default: return 5000;
    }
  }
}

export const notifications = new NotificationManager();

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => notifications.init());
  else notifications.init();
}
